/**
 * Inglês. Segue a estrutura de ./pt-BR.ts (o TypeScript valida as chaves); o que faltar cai no pt-BR.
 * Rótulos citados na documentação em inglês (packages/docs/content/en) usam o mesmo texto da doc.
 */
import type { PartialDict } from "./index";

export const en: PartialDict = {
  common: {
    appName: "Tenanci",
    loading: "Loading…",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    deleting: "Deleting…",
    edit: "Edit",
    create: "Create",
    add: "Add",
    back: "Back",
    next: "Continue",
    retry: "Try again",
    copy: "Copy",
    copied: "Copied!",
    copyValue: "Copy {label}",
    confirm: "Confirm",
    enabled: "Enabled",
    disabled: "Disabled",
    optional: "optional",
    actions: "Actions",
    moreActions: "More actions",
    learnMore: "Learn more",
    published: "Published",
    publishedDesc: "The change goes live in a few seconds.",
    errorTitle: "Something went wrong",
    moveUp: "Move up",
    moveDown: "Move down",
    loadMore: "Load more",
    never: "never",
    or: "or",
    none: "—",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    language: "Language",
    languageCurrent: "Language: {lang}",
    skipToContent: "Skip to content",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    typeToConfirm: "Type {value} to confirm",
    comingSoon: "Coming soon",
  },
  nav: {
    main: "Main navigation",
    home: "Home",
    projects: "Projects",
    billing: "Plan & billing",
    apiKeys: "API keys",
    account: "My account",
    organization: "Organization",
    admin: "Administration",
    docs: "Help & docs",
    reportBug: "Report a problem",
    signOut: "Sign out",
  },
  admin: {
    title: "Platform administration",
    subtitle: "Organizations (complimentary plans and origin verification) and TenantGuard. Every action is recorded in the audit log.",
    search: "Search by organization, owner email or origin",
    searchButton: "Search",
    empty: "No organizations found.",
    forbidden: "This area is restricted to platform administrators.",
    noSubscription: "No subscription",
    comp: "complimentary",
    stripe: "Stripe",
    plan: "Plan",
    apply: "Grant complimentary plan",
    removeComp: "End complimentary plan",
    planApplied: "Complimentary plan granted",
    compRemoved: "Complimentary plan ended",
    stripeManaged: "Subscription managed by Stripe: change it in the portal or in Stripe.",
    projects: "Projects",
    noProjects: "No projects.",
    verified: "Origin verified",
    verify: "Mark origin as verified",
    verifyTitle: "Verify origin without the TXT record?",
    verifyDesc: "The origin {origin} will be treated as the customer's without DNS proof. Only do this after confirming with the customer.",
    projectVerified: "Origin marked as verified",
    domains: "{n} domain(s)",
    failed: "Could not complete",
    tabs: { orgs: "Organizations", crowdsec: "TenantGuard", jobs: "Jobs", betaCodes: "Beta codes" },
    suspended: "Suspended",
    suspend: "Suspend",
    unsuspend: "Reactivate",
    suspendTitle: "Suspend {org}?",
    suspendDesc: "Every domain in this organization goes offline (visitors see the suspended-domain page). Use it for abuse, phishing or fraud.",
    suspendReason: "Reason (kept in the audit log)",
    suspendedOk: "Organization suspended",
    unsuspendedOk: "Organization reactivated",
    impersonate: "Access as customer",
    impersonateHint: "Opens this organization's dashboard. Everything you do is recorded in its activity as Tenanci team.",
    jobs: {
      desc: "Jobs that failed for good in the last 14 days (DNS, certificate, publishing, webhooks…). Retrying creates a new job with the same data.",
      state: "State",
      states: { failed: "Failed", retry: "Retrying", active: "Running" },
      empty: "No jobs in this state.",
      queue: "Queue",
      when: "When",
      attempts: "Attempts",
      error: "Error",
      data: "Data",
      retry: "Retry",
      retried: "Job retried",
    },
    beta: {
      desc: "Codes that unlock beta pricing at checkout (one use per code).",
      count: "Quantity",
      prefix: "Prefix",
      note: "Note",
      create: "Generate codes",
      created: "{n} code(s) generated",
      empty: "No codes yet.",
      code: "Code",
      usedBy: "Used by",
      unused: "Available",
      active: "Active",
      inactive: "Disabled",
      disable: "Disable",
      enable: "Enable",
    },
    crowdsec: {
      notConfigured: "TenantGuard management is off",
      notConfiguredDesc:
        "Set CROWDSEC_PANEL_PASSWORD (same value) on the crowdsec-lapi, server and worker containers of tenanci-core and restart. The LAPI registers the dashboard machine by itself.",
      tabs: { overview: "Overview", decisions: "Bans", alerts: "Alerts", allowlist: "Allowed IPs" },
      lapi: "LAPI",
      online: "Online",
      offline: "Offline",
      unknown: "No data",
      last24h: "Last 24 hours",
      alerts: "Alerts",
      waf: "WAF blocks",
      bans: "Bans",
      uniqueIps: "Distinct IPs",
      activeBans: "Active bans",
      manualBans: "{n} manual",
      noAnalytics: "History unavailable: analytics (ClickHouse) did not respond.",
      timeline: "Alerts per hour",
      seriesAlerts: "Alerts",
      seriesBans: "Bans",
      topScenarios: "Most common attacks",
      topDomains: "Most attacked domains",
      topIps: "Most active IPs",
      components: "Components",
      machines: "Agents (machines)",
      bouncers: "Bouncers",
      edges: "Edge nodes",
      lastSeen: "Last seen",
      version: "Version",
      never: "never",
      unreadable: "Could not read the LAPI database.",
      search: "Search by IP, reason or domain",
      ban: "Ban IP",
      banTitle: "Ban an IP or range",
      banDesc: "The ban applies to every edge within 10 seconds.",
      ipLabel: "IP or range (CIDR)",
      ipHint: "E.g. 203.0.113.7 or 203.0.113.0/24 (at least /16).",
      duration: "Duration",
      durations: { "1h": "1 hour", "4h": "4 hours", "24h": "24 hours", "7d": "7 days", "30d": "30 days", "365d": "1 year", "1d": "1 day", "90d": "90 days", never: "No expiry" },
      reason: "Reason",
      reasonPlaceholder: "E.g. checkout scanning",
      banned: "IP banned",
      unban: "Unban",
      unbanTitle: "Unban {ip}?",
      unbanDesc: "The IP can reach every domain again. If it keeps attacking, TenantGuard may ban it again.",
      unbanned: "Ban removed",
      noDecisions: "No active bans.",
      ip: "IP",
      motive: "Reason",
      origin: "Source",
      originAuto: "Automatic",
      originManual: "Manual",
      domains: "Domains",
      created: "Started",
      expires: "Expires",
      actions: "Actions",
      showing: "{shown} of {total}",
      period: "Period",
      periods: { "24": "24 hours", "168": "7 days", "720": "30 days", "2160": "90 days" },
      kind: "Type",
      kinds: { all: "All", waf: "WAF", scenario: "Behavior", manual: "Manual" },
      filterIp: "IP",
      filterHost: "Domain",
      filter: "Filter",
      clear: "Clear",
      noAlerts: "No alerts match these filters.",
      when: "When",
      attack: "Attack",
      request: "Request",
      result: "Result",
      resultBan: "IP banned",
      resultWaf: "Blocked",
      resultAlert: "Detected",
      details: "Details",
      userAgent: "User-Agent",
      network: "Network",
      events: "Events",
      machine: "Agent",
      message: "Message",
      allow: "Allow IP",
      allowTitle: "Allow an IP or range",
      allowDesc:
        "Allowed IPs are never banned and the WAF does not inspect their requests. Use it for offices, monitoring or customer integrations. Applies to every edge within 1 minute.",
      comment: "Comment",
      commentPlaceholder: "E.g. Bree customer office",
      validity: "Valid for",
      allowed: "IP allowed",
      allowedUnbanned: "IP allowed and {n} ban(s) removed",
      unallow: "Remove",
      unallowTitle: "Remove {ip} from the list?",
      unallowDesc: "The IP goes through the WAF again and may be banned by TenantGuard.",
      unallowed: "IP removed from the list",
      noAllowlist: "No allowed IPs.",
      expiresNever: "No expiry",
      createdBy: "Allowed by",
      refresh: "Refresh",
    },
  },
  errors: {
    unauthorized: "Your session has expired. Please sign in again.",
    forbidden: "You don't have permission to do this.",
    not_found: "We couldn't find what you were looking for. It may have been deleted.",
    subscription_required: "Your subscription isn't active. Choose a plan to continue.",
    feature_not_in_plan: "This feature isn't included in your plan. Upgrade to use it.",
    domain_limit_reached: "You've reached your plan's domain limit. Accept the extra domain pack or change plans.",
    hostname_taken: "This domain is already in use in another Tenanci account. Contact support to transfer it.",
    domain_exists: "This domain is already registered in your account.",
    invalid_hostname: "Invalid domain. Use something like app.yourcustomer.com.",
    invalid_origin: "Invalid origin URL. Use a public address, such as https://app.yoursaas.com.",
    invalid_extra_origin: "One of the extra origins is invalid.",
    invalid_route: "Invalid route: source and destination must be paths starting with /.",
    invalid_header: "Invalid header. The name can only contain letters, numbers and - _ .",
    invalid_injection: "The HTML content is invalid or too large.",
    invalid_ip: "Invalid IP. Use an IP (203.0.113.7) or a CIDR range (203.0.113.0/24).",
    range_too_wide: "Range too wide: the minimum is /16 (IPv4) or /32 (IPv6).",
    allowlisted: "This IP is on the allowlist: remove it from there before banning.",
    already_allowlisted: "This IP is already on the allowlist.",
    crowdsec_unavailable: "TenantGuard (LAPI) did not respond. Try again in a moment.",
    crowdsec_not_configured: "TenantGuard management isn't configured (CROWDSEC_PANEL_PASSWORD).",
    analytics_unavailable: "History (analytics) is unavailable right now.",
    invalid_path: "The path pattern must start with /.",
    invalid_seo: "Invalid SEO data (check the image URL).",
    invalid_rules: "The TailorKit rules are in an invalid format.",
    rules_too_large: "The TailorKit rules exceed 512 KB. Remove some changes.",
    no_draft: "There's no draft to publish. Save first.",
    no_subscription: "You don't have a subscription yet. Subscribe first.",
    already_subscribed: "Your organization already has an active subscription. Use the plan switch instead.",
    invalid_beta_code: "Invalid or already used beta code.",
    billing_unavailable: "Billing is unavailable right now. Try again in a few minutes.",
    plan_not_configured: "This plan isn't available for purchase yet.",
    storage_unavailable: "File uploads aren't available. Use a URL instead.",
    missing_file: "Select a file.",
    invalid_size: "The file is empty or larger than 1 MB.",
    invalid_type: "Unsupported format. Upload a PNG, ICO, SVG, WEBP, JPG or GIF.",
    unsafe_svg: "SVGs with scripts aren't allowed.",
    validation_error: "Some data is invalid. Check the fields.",
    invalid_json: "Invalid request.",
    idempotency_key_reused: "This operation was already sent with different data. Reload the page.",
    rate_limited: "Too many attempts in a short time. Wait a moment.",
    internal_error: "Internal server error. We've been notified — try again in a moment.",
    network: "Can't reach the server. Check your internet connection.",
    unknown: "Something went wrong. Please try again.",
  },
  authErrors: {
    INVALID_EMAIL_OR_PASSWORD: "Incorrect email or password.",
    EMAIL_NOT_VERIFIED: "Confirm your email before signing in.",
    USER_ALREADY_EXISTS: "An account with this email already exists.",
    USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "An account with this email already exists.",
    PASSWORD_TOO_SHORT: "The password must be at least 10 characters.",
    PASSWORD_TOO_LONG: "The password is too long.",
    INVALID_PASSWORD: "Incorrect current password.",
    INVALID_EMAIL: "Invalid email.",
    INVALID_TOKEN: "The link has expired or is invalid. Request a new one.",
    TOKEN_EXPIRED: "The link has expired. Request a new one.",
    CHANGE_EMAIL_DISABLED: "Changing your email isn't available yet. Contact support.",
    SESSION_EXPIRED: "For your security, sign in again to make this change.",
    CREDENTIAL_ACCOUNT_NOT_FOUND: "Your account doesn't have a password yet. Use “Forgot my password” to create one.",
    USER_NOT_FOUND: "We couldn't find an account with this email.",
    INVALID_ORIGIN: "Unauthorized origin. Reload the page.",
  },
  hostname: {
    empty: "Enter the domain.",
    invalid_format: "Invalid format. Use something like app.yourcustomer.com.",
    ip_not_allowed: "Use a domain name, not an IP address.",
    not_public_suffix: "This doesn't look like a valid public domain.",
    reserved: "Tenanci domains can't be registered.",
    too_long: "Domain too long.",
    apex: "Root domain (apex): set it up with an A record.",
    subdomain: "Subdomain of {base}: set it up with a CNAME record.",
    willSaveAs: "We'll register it as {hostname}.",
    wwwHint: "Tip: www.{host} and {host} are different domains. Add both if you need them.",
  },
  validation: {
    required: "Required field.",
    email: "Enter a valid email.",
    passwordMin: "The password must be at least 10 characters.",
    passwordMismatch: "The passwords don't match.",
    path: "Use a path starting with /, e.g. /pricing.",
    url: "Enter a full URL, starting with https://.",
    headerName: "Use only letters, numbers and - _ .",
    json: "Invalid JSON.",
  },
  auth: {
    tagline: "Your app on your customers' domains.",
    email: "Email",
    password: "Password",
    name: "Name",
    login: {
      title: "Sign in to Tenanci",
      subtitle: "Welcome back!",
      submit: "Sign in",
      submitting: "Signing in…",
      forgot: "Forgot my password",
      magicLink: "Email me a sign-in link",
      magicLinkSubmit: "Send sign-in link",
      usePassword: "Sign in with password",
      magicSentTitle: "Link sent!",
      magicSentDesc: "If there's an account for {email}, you'll get a sign-in link valid for 15 minutes.",
      noAccount: "No account yet?",
      signupLink: "Create account",
      notVerified: "Your email hasn't been confirmed yet.",
      resend: "Resend confirmation email",
      resent: "We sent a new confirmation link.",
      migratedHint: "Coming from the old dashboard? Use “Email me a sign-in link” the first time.",
    },
    signup: {
      title: "Create your account",
      subtitle: "It takes less than a minute.",
      passwordHint: "At least 10 characters.",
      submit: "Create account",
      submitting: "Creating…",
      hasAccount: "Already have an account?",
      loginLink: "Sign in",
      terms: "By creating an account you agree to Tenanci's Terms of Use and Privacy Policy.",
    },
    verifyEmail: {
      title: "Check your email",
      desc: "We sent a confirmation link to {email}. Click it to activate your account.",
      descNoEmail: "We sent a confirmation link to your email. Click it to activate your account.",
      tip: "Didn't get it within a few minutes? Check your spam folder or resend it.",
      resend: "Resend email",
      resent: "Done! We sent another link.",
      backToLogin: "Back to sign in",
    },
    forgot: {
      title: "Forgot your password?",
      desc: "Enter your email and we'll send you a link to set a new password.",
      submit: "Send link",
      sentTitle: "Check your email",
      sentDesc: "If there's an account for {email}, you'll get the link shortly.",
    },
    reset: {
      title: "Set a new password",
      newPassword: "New password",
      confirmPassword: "Confirm the new password",
      submit: "Save new password",
      success: "Password changed! Sign in with your new password.",
      invalidToken: "This link has expired or was already used.",
      requestNew: "Request a new link",
    },
    callback: {
      signingIn: "Signing in…",
      errorTitle: "Couldn't sign you in",
      errorDesc: "The link has expired or was already used. Request a new one.",
      backToLogin: "Back to sign in",
    },
  },
  onboarding: {
    title: "Let's set up your account",
    stepOf: "Step {current} of {total}",
    steps: {
      profile: "Profile",
      plan: "Plan",
      project: "First project",
    },
    profileTitle: "How will you use Tenanci?",
    profiles: {
      saas_owner: { title: "SaaS owner", desc: "I want to offer my app on my customers' domains." },
      agency: { title: "Agency", desc: "I build and manage apps for several clients." },
      reseller: { title: "Reseller", desc: "I resell white-label solutions under my own brand." },
    },
    referralTitle: "How did you find us?",
    referrals: {
      comunidade_semcodar: "SemCodar community",
      construindo_startup: "Construindo uma Startup",
      saas_7d: "SaaS 7D - Founders",
      indicacao: "A friend's recommendation",
      google_ads: "Google ads",
      instagram_ads: "Instagram ads",
      facebook_ads: "Facebook ads",
      outro: "Other",
    },
    referralOther: "Tell us briefly",
    orgName: "Your company name",
    orgNameHint: "Shown on invoices and in the dashboard.",
    pickProfile: "Choose an option.",
    saveFailed: "We couldn't save your answers right now, but you can continue — you can edit them later under Organization.",
    planTitle: "Choose your plan",
    planDesc: "You can switch plans whenever you want, without losing anything.",
    cardNoticeTitle: "Why do we ask for a card even on the free plan?",
    cardNotice:
      "Every plan requires a card, including the free Starter. On Starter you pay nothing: the card only confirms you're a real person and protects the platform (and your customers) against abuse and phishing. Extra domains are only charged when you add them, and the dashboard always shows the price first.",
    continueToStripe: "Continue to secure payment",
    redirecting: "Opening Stripe…",
    stripeNote: "Payment processed by Stripe. Tenanci doesn't store your card details.",
    betaCode: "Beta code",
    betaCodeToggle: "I have a beta code",
    waitingActivation: "Confirming your subscription… this only takes a few seconds.",
    projectTitle: "Create your first project",
    projectDesc: "A project is the app you want to serve on white-label domains (e.g. your SaaS built on Bubble or FlutterFlow).",
    finish: "Create project",
    skip: "Do this later",
    doneTitle: "All set!",
  },
  gate: {
    inactiveBanner: "Your subscription isn't active. Choose a plan to unlock the dashboard.",
    pastDueBanner: "Payment overdue. Update your card in the payment portal to avoid having your domains suspended.",
    seePlans: "See plans",
    openPortal: "Update payment",
  },
  billing: {
    compNote: "Complimentary plan granted by Tenanci: no charge and no card. To change it, contact support.",
    compPlansNote: "Your organization is on a complimentary plan. Plan changes go through Tenanci support.",
    title: "Plan & billing",
    subtitle: "Your plan, domain usage, card and invoices.",
    currentPlan: "Current plan",
    noPlan: "You don't have an active plan yet",
    noPlanDesc: "Choose a plan below to unlock the dashboard.",
    usage: "Domains in use",
    usageOf: "{used} of {capacity}",
    packs: "{included} included + {n} pack(s) of {size}: {price}/month",
    autoPacks: "Buy the next pack automatically when the API or an AI agent goes over the limit (with an email notice; in the dashboard we always ask)",
    autoPacksSaved: "Preference saved",
    renewsOn: "Renews on {date}",
    cancelsOn: "Will be canceled on {date}",
    manage: "Manage payment and invoices",
    manageDesc: "Changing your card, downloading invoices and canceling happen in Stripe's secure portal.",
    opening: "Opening…",
    plans: "Plans",
    currentBadge: "Your plan",
    choose: "Subscribe to {plan}",
    changeTo: "Switch to {plan}",
    perMonth: "/month",
    free: "Free",
    includedDomains: "{n} domains included",
    extraPack: "+{size} domains for {price}/month (extra pack)",
    noExtra: "No extra domains: change plans for more",
    runningTests: "Up to {n} landing page A/B tests running",
    unavailable: "Coming soon",
    status: {
      incomplete: "Payment incomplete",
      trialing: "Trial",
      active: "Active",
      past_due: "Payment overdue",
      unpaid: "Unpaid",
      canceled: "Canceled",
    },
    checkoutSuccess: "Payment confirmed! We're activating your subscription…",
    checkoutActivated: "Subscription active. All set!",
    checkoutSlow: "We're still waiting for Stripe's confirmation. It can take a minute — reload in a moment.",
    checkoutCanceled: "Payment canceled. No charge was made.",
    changeTitle: "Switch to the {plan} plan?",
    changeDesc: "The price difference is prorated and shows up on your next invoice. The card on file is used automatically.",
    changeOverQuota: "Heads up: you use {used} domains and the {plan} plan includes {included}. The extra ones go into packs of {size} domains ({price}/month each).",
    changeBlocked: "The {plan} plan fits {included} domains and you use {used}. Remove domains before switching.",
    changed: "Plan changed! It may take a few seconds to update here.",
    preview: {
      loading: "Calculating the price…",
      monthly: "New monthly price: {amount}/month",
      dueNow: "Prorated charge today: {amount}",
      packs: "includes {n} pack(s) of {size} domains (up to {capacity})",
      unavailable: "Couldn't calculate the price right now.",
    },
    cardRequired: "A card is required on every plan, including Starter (no charge).",
    features: {
      starter: ["TenantGuard (WAF + IDPS)", "Basic analytics", "Request customization"],
      landing: ["Everything in Starter", "Landing page A/B tests (with automatic traffic)", "Advanced analytics, goals and funnels", "Server-side conversions (Meta, Google, TikTok)"],
      growth: ["Everything in Landing", "TailorKit (visual editor)", "Advanced SEO", "Customer portal, installable app and branded email"],
      scale: ["Everything in Growth", "AuthMesh", "API Gateway"],
    },
  },
  home: {
    greeting: "Hi, {name}!",
    subtitle: "A quick summary of your account.",
    projects: "Your projects",
    seeAll: "See all",
    newProject: "New project",
    noProjects: "You don't have any projects yet. Create the first one to get started.",
    usage: "Domain usage",
    plan: "Plan",
    docs: "API & integrations",
    docsDesc: "Automate domains from your app with the Tenanci API.",
    news: "What's new",
    newsItems: [
      "New dashboard: customizations go live instantly — no more “Save changes”.",
      "Live domain status, with a history of every step (DNS, certificate, active).",
      "SEO per domain and per page, with a social card preview.",
    ],
  },
  projects: {
    title: "Projects",
    subtitle: "Each project is an origin app served on many white-label domains.",
    new: "New project",
    emptyTitle: "No projects yet",
    emptyDesc: "Create a project with your app's URL to start adding domains.",
    domainsCount: "{n} domain(s)",
    name: "Project name",
    namePlaceholder: "E.g. Craft CRM",
    origin: "Origin URL",
    originPlaceholder: "https://app.yoursaas.com",
    originHint: "The address where your app runs today.",
    create: "Create project",
    creating: "Creating…",
    created: "Project created! Now confirm the origin is yours.",
    status: {
      pending_verification: "Pending verification",
      active: "Active",
      disabled: "Disabled",
    },
    tabs: {
      overview: "Overview",
      domains: "Domains",
      customization: "Customization",
      status: "Status",
      integration: "Integration",
      portal: "Customer portal",
      apis: "APIs",
    },
    deleteTitle: "Delete project",
    deleteDesc: "This takes the project and all of its domains offline. It can't be undone.",
    deleteAction: "Delete project",
    deleted: "Project deleted.",
    dangerZone: "Danger zone",
  },
  ownership: {
    title: "Ownership verification",
    desc: "To prevent misuse (e.g. phishing), confirm the origin is yours by creating this TXT record in the DNS of {domain}. No domain goes live before that.",
    verified: "Origin verified",
    verifiedAt: "Verified on {date}",
    pending: "Pending",
    type: "Type",
    name: "Name / Host",
    value: "Value",
    verifyNow: "Verify now",
    checking: "Checking…",
    success: "All good! Ownership verified.",
    notFound: "We haven't found the record yet. DNS propagation can take a few minutes.",
    foundValues: "TXT values found at {name}:",
    noneFound: "No TXT record found at {name}.",
    providerHint: "Some providers (Registro.br, GoDaddy, Hostinger) append the domain on their own. In that case, enter only {short} in the name field.",
  },
  overview: {
    origin: "Origin",
    originDesc: "The app served on this project's domains.",
    originChangeWarning: "Switching to another domain requires a new ownership verification.",
    general: "Project details",
    saved: "Settings saved",
    ids: "Identifiers",
    projectId: "Project ID",
    legacyId: "Legacy ID (Bubble)",
    tailorkitHost: "TailorKit host",
    spa: {
      title: "Single-page app (SPA)",
      desc: "For Flutter, React or Vue apps that navigate without reloading the page.",
      mode: "Navigation mode",
      mask: "Mask",
      maskDesc: "Visitors always see your customer's domain in the address bar.",
      redirect: "Redirect",
      redirectDesc: "Routes are redirected (302). Use it if the app has trouble with masked mode.",
      initialRouteVar: "Send initial route in a variable",
      initialRouteVarDesc: "Useful for Flutter Web: the requested route is passed to the app on startup.",
    },
    extraOrigins: {
      title: "Extra origins",
      desc: "Additional hosts your app loads (CDN, API, storage) that should go through the proxy.",
      placeholder: "cdn.yoursaas.com",
      add: "Add origin",
      empty: "No extra origins.",
      remove: "Remove {host}",
      invalid: "Invalid host",
      duplicate: "This host is already on the list",
    },
  },
  domains: {
    title: "Domains",
    subtitle: "White-label domains serving this project.",
    add: "Add domain",
    emptyTitle: "No domains yet",
    emptyDesc: "Add your customer's domain. We'll show you exactly which DNS record to create.",
    col: {
      name: "Name",
      hostname: "Domain",
      status: "Status",
      created: "Created",
    },
    menu: {
      open: "View details",
      edit: "Edit",
      disable: "Disable",
      enable: "Enable",
      recheck: "Check DNS again",
      tailorkit: "Open TailorKit",
      delete: "Delete",
    },
    disabledToast: "Domain disabled.",
    enabledToast: "Domain re-enabled.",
    recheckToast: "New DNS check started.",
    deleteTitle: "Delete domain",
    deleteDesc: "{hostname} goes offline and its certificate is revoked. It can't be undone.",
    deleted: "Domain deleted.",
    editTitle: "Edit domain",
    status: {
      pending_dns: "Waiting for DNS",
      verifying: "Verifying",
      issuing_cert: "Issuing certificate",
      active: "Active",
      suspended: "Suspended",
      error: "Error",
      removed: "Removed",
      unknown: "Unknown",
    },
    statusDesc: {
      pending_dns: "Set up the DNS record below. We check automatically.",
      verifying: "Confirming domain ownership.",
      issuing_cert: "DNS found! Issuing the HTTPS certificate (usually 1 to 2 minutes).",
      active: "Live, with HTTPS.",
      suspended: "Temporarily offline.",
      error: "Something prevented activation. See the reason and try again.",
      removed: "Domain removed.",
      unknown: "",
    },
    reasons: {
      created: "Domain registered",
      dns_ok: "DNS record found",
      dns_timeout: "DNS record not found within 7 days",
      cert_issued: "HTTPS certificate issued",
      cert_timeout: "Timed out issuing the certificate",
      cert_failed: "Failed to issue the certificate",
      user_disabled: "Disabled by you",
      user_enabled: "Re-enabled by you",
      recheck_requested: "New check requested",
      deleted: "Deleted",
      billing: "Inactive subscription",
      tailorkit_viewer: "TailorKit editor host",
    },
    addTitle: "Add domain",
    addDesc: "Use your customer's domain (or subdomain). Next we'll show the DNS record to create.",
    hostname: "Domain",
    hostnamePlaceholder: "app.yourcustomer.com",
    displayName: "Display name",
    displayNamePlaceholder: "E.g. Silence customer",
    quotaOk: "You use {used} of {capacity} domains on your plan.",
    quotaExtra: "You already use {used} of {capacity} domains. This domain adds a pack of {size} domains: {price}/month.",
    quotaUpgrade: "You already use {used} of {capacity} domains, your plan's limit. To add more, change plans.",
    seePlans: "See plans",
    acceptExtra: "I agree to the pack of {size} domains for {price}/month.",
    acceptRequired: "Confirm the domain pack to continue.",
    ownConfig: "Use its own configuration (don't inherit the project's customizations)",
    submit: "Add domain",
    submitting: "Adding…",
    created: "Domain added! Now set up DNS.",
    verifyProjectFirst: "The project's origin hasn't been verified yet. You can add domains now, but they only go live after verification.",
  },
  domain: {
    back: "Domains",
    dnsTitle: "DNS setup",
    dnsDesc: "Create this record at the domain's DNS provider (Registro.br, Cloudflare, GoDaddy…).",
    dnsApexMulti: "Create one A record for each IP below.",
    type: "Type",
    name: "Name",
    value: "Value",
    ttl: "TTL: automatic (or 300 seconds).",
    cloudflareHint: "Using Cloudflare? Keep the cloud grey (“DNS only”), with no proxy.",
    lastCheck: "Last check: {date}",
    timelineTitle: "Timeline",
    live: "Updating live",
    noEvents: "No events yet.",
    recheck: "Check again",
    openTailorkit: "Open TailorKit",
    experiments: "A/B tests",
    tabs: {
      setup: "Setup",
      settings: "Settings",
      customization: "Customization",
      seo: "SEO",
      marketing: "Marketing",
      app: "Installable app",
      access: "Access",
    },
    settings: {
      title: "Domain settings",
      name: "Display name",
      saved: "Settings saved",
    },
    waf: {
      title: "TenantGuard (WAF)",
      desc: "Application firewall that protects the domain against common attacks.",
      mode: "Mode",
      off: "Off",
      monitor: "Monitor (log only)",
      block: "Block attacks",
      failMode: "If the WAF becomes unavailable",
      open: "Let traffic through (favors availability)",
      closed: "Block (favors security)",
    },
    performance: {
      title: "Performance",
      desc: "The app's JS and CSS files that don't change are kept at the edge, already compressed at the highest level.",
      direct: "Static files straight from the origin",
      directHint:
        "Saves traffic: browsers download the JS/CSS, fonts and images that don't change straight from the app's server (only the ones that need no rewriting). The app's original address shows up in those files' requests; turn it off if the customer must not see it.",
      htmlCache: "HTML cache",
      htmlCacheHint:
        "For sites where the page is the same for every visitor (single-page apps: React, Vue, Lovable…). The edge keeps the HTML and answers instantly, even when the app's server is slow, refreshing it in the background. Don't use it on Bubble apps or on sites that build the page with the signed-in user's data.",
      htmlCacheOff: "Off",
      htmlCache60: "1 minute",
      htmlCache300: "5 minutes",
      htmlCache3600: "1 hour",
    },
    favicon: {
      title: "Favicon",
      desc: "Icon shown in the browser tab for this domain.",
      url: "Favicon URL",
      upload: "Upload file",
      uploading: "Uploading…",
      uploadHint: "PNG, ICO, SVG, WEBP, JPG or GIF, up to 1 MB.",
      remove: "Remove favicon",
      saved: "Favicon updated",
      removed: "Favicon removed",
      unknownCurrent: "The current favicon isn't shown here yet.",
    },
    overrides: {
      title: "Configuration inheritance",
      desc: "By default this domain uses the project's customizations. Turn on a section to use rules for this domain only.",
      inherit: "Inheriting from project",
      own: "Own configuration",
      toggle: "Use own configuration for {section}",
      inheritedRules: "Rules inherited from the project (read-only):",
      editInProject: "Edit in project",
      switched: "Inheritance updated",
    },
    dangerZone: "Danger zone",
    deleteDesc: "Takes the domain offline and revokes its certificate.",
  },
  sections: {
    routes: "Routes",
    headers: "Headers",
    injections: "HTML injection",
  },
  rules: {
    toggle: "Turn rule on or off",
    deleteConfirm: "Delete this rule?",
    deleted: "Rule deleted",
    empty: "No rules yet.",
    routes: {
      desc: "Map friendly paths on the domain to paths on the origin.",
      source: "Path on the domain",
      sourcePlaceholder: "/pricing",
      destination: "Path on the origin",
      destinationPlaceholder: "/pricing",
      mode: "Mode",
      rewrite: "Rewrite (masked URL)",
      redirect: "Redirect (302)",
      add: "Add route",
    },
    headers: {
      desc: "HTTP headers added to responses (for the browser) or to requests (for the origin).",
      name: "Name",
      namePlaceholder: "X-Frame-Options",
      value: "Value",
      valuePlaceholder: "SAMEORIGIN",
      direction: "Direction",
      response: "Response (browser)",
      request: "Request (origin)",
      add: "Add header",
    },
    injections: {
      desc: "Scripts and tags (analytics, chat, pixels) inserted into every page.",
      position: "Where to insert",
      head: "In the <head>",
      body: "At the end of the <body>",
      content: "HTML code",
      contentPlaceholder: "<script>/* your code */</script>",
      add: "Add injection",
      collapse: "Collapse",
      expand: "Edit code",
    },
  },
  customization: {
    tenants: "Customers",
    title: "Customization",
    desc: "Applies to every domain in the project, except those with their own configuration. Changes go live instantly.",
  },
  seo: {
    title: "SEO & sharing",
    desc: "The title, description and image shown on Google and when the link is shared.",
    default: "Domain default",
    defaultDesc: "Used on every page that doesn't have its own pattern.",
    paths: "Per page",
    pathsDesc: "More specific patterns win. Use * at the end for prefixes (e.g. /blog/*).",
    addPath: "Add page",
    pathPattern: "Path pattern",
    pathPlaceholder: "/pricing",
    pageTitle: "Title",
    description: "Description",
    image: "Image URL",
    imageHint: "Recommended: 1200 × 630 px.",
    robots: "Robots",
    robotsHint: "E.g. noindex, nofollow (leave empty for the default).",
    jsonLd: "JSON-LD",
    jsonLdHint: "Structured data (optional).",
    preview: "Social card preview",
    saved: "SEO saved",
    deleted: "Pattern removed",
    removeTitle: "Remove the SEO for {path}?",
    noPaths: "No per-page patterns.",
    chars: "{n}/{max}",
  },
  apiKeys: {
    title: "API keys",
    subtitle: "Use the API to create and manage domains straight from your app.",
    docs: "View documentation",
    new: "New key",
    emptyTitle: "No keys yet",
    emptyDesc: "Create a key to connect your app (Bubble, n8n, your own backend) to Tenanci.",
    col: {
      name: "Name",
      key: "Key",
      project: "Project",
      scopes: "Scopes",
      lastUsed: "Last used",
      created: "Created",
    },
    allProjects: "All projects",
    revoke: "Revoke",
    revokeTitle: "Revoke the key {name}?",
    revokeDesc: "Integrations using this key stop working immediately.",
    revoked: "Key revoked.",
    createTitle: "New API key",
    name: "Name",
    namePlaceholder: "E.g. Bubble integration (production)",
    project: "Restrict to a project",
    projectHint: "Restricted keys only see one project. Recommended.",
    scopes: "Scopes",
    scopeLabels: {
      "domains:read": "Read domains",
      "domains:write": "Manage domains",
      "projects:read": "Read projects",
      "projects:write": "Manage projects",
    },
    scopesRequired: "Choose at least one scope.",
    create: "Create key",
    secretTitle: "Copy your key now",
    secretDesc: "For security, this is the only time we show the full key. Store it somewhere safe.",
    secretDone: "I've copied it, close",
    rotate: "Rotate",
    rotateTitle: "Rotate the key {name}?",
    rotateDesc: "We create a new key with the same scopes. The current one keeps working for the time you choose, so you can swap it in your systems without downtime.",
    grace: "Keep the current key working for",
    graceOptions: { "0": "Stop now", "1": "1 hour", "24": "24 hours", "168": "7 days" },
    rotated: "New key created.",
    expires: "valid until {date}",
  },
  account: {
    title: "My account",
    profile: "Profile",
    name: "Name",
    profileSaved: "Profile updated",
    email: "Email",
    verified: "verified",
    notVerified: "not verified",
    changeEmail: "Change email",
    newEmail: "New email",
    changeEmailSent: "We sent a confirmation link to the new email.",
    password: "Password",
    currentPassword: "Current password",
    newPassword: "New password",
    changePassword: "Change password",
    passwordChanged: "Password changed.",
    revokeOthers: "Sign out of other open sessions",
    preferences: "Preferences",
    session: "Session",
    signOut: "Sign out",
    signOutDesc: "Ends the session in this browser.",
  },
  organization: {
    title: "Organization",
    subtitle: "Your company's details on Tenanci.",
    name: "Organization name",
    profile: "Profile",
    referral: "How you found us",
    saved: "Organization updated",
    notSaved: "Couldn't save right now (feature not yet available on the server).",
    members: "Members & invitations",
    membersSoon: "Soon you'll be able to invite people from your team, with owner, admin or member roles.",
    tabs: { general: "General", team: "Team", webhooks: "Webhooks", activity: "Activity" },
    notifyTitle: "Email notifications",
    notifyDomainStatus: "Notify owners and admins when a domain goes live, fails or is suspended",
    notifySaved: "Preference saved",
    emailLocale: "Email language",
    emailLocaleHint: "Domain notices, invitations and the monthly report to your customers.",
    suspendedBanner: "This organization is suspended and its domains are offline. Contact support@tenanci.com.",
    impersonatingBanner: "You're viewing the {org} organization as Tenanci team. Everything you do is recorded in its activity.",
    impersonatingExit: "Back to my organization",
    switchOrg: "Switch organization",
    team: {
      title: "Team",
      desc: "People with access to this organization. Owners can do everything; admins manage projects, domains and the team (except owners); members use the dashboard.",
      you: "you",
      since: "since {date}",
      role: "Role",
      remove: "Remove",
      leave: "Leave organization",
      removeTitle: "Remove {name}?",
      removeDesc: "This person loses access to this organization immediately.",
      leaveTitle: "Leave {org}?",
      leaveDesc: "You'll lose access to this organization. To come back, someone has to invite you again.",
      removed: "Person removed",
      roleChanged: "Role changed",
      inviteTitle: "Invite someone",
      inviteDesc: "We email a link valid for 7 days. The person signs in (or creates an account) with that email.",
      email: "Email",
      invite: "Invite",
      invited: "Invitation sent to {email}",
      invitedNoEmail: "Invitation created. The email couldn't be sent: copy the link and send it to the person.",
      inviteLink: "Invitation link",
      pending: "Pending invitations",
      expires: "valid until {date}",
      revoke: "Cancel invitation",
      revoked: "Invitation canceled",
      noPending: "No pending invitations.",
    },
    webhooks: {
      title: "Webhooks",
      desc: "Notify your systems when something happens on Tenanci (e.g. a domain went live). We send a signed JSON POST to your URL.",
      empty: "No webhooks yet.",
      add: "New webhook",
      url: "URL (https)",
      description: "Description (optional)",
      events: "Events",
      eventNames: {
        domain_created: "Domain added",
        domain_status_changed: "Domain status changed",
        domain_deleted: "Domain removed",
        tailorkit_published: "TailorKit published",
      },
      create: "Create webhook",
      created: "Webhook created",
      secretTitle: "Signing secret",
      secretDesc: "Save it now: it won't be shown again. Use it to check the Tenanci-Signature header (HMAC-SHA256 of \"<t>.<body>\").",
      enabled: "Enabled",
      test: "Send test",
      tested: "Test sent. Check the result under deliveries in a few seconds.",
      rotate: "Rotate secret",
      rotateTitle: "Generate a new secret?",
      rotateDesc: "The current secret stops working immediately. Update your system with the new one.",
      remove: "Remove",
      removeTitle: "Remove this webhook?",
      removed: "Webhook removed",
      deliveries: "Deliveries",
      noDeliveries: "No deliveries yet.",
      status: { pending: "Pending", success: "Delivered", failed: "Failed" },
      attempts: "{n} attempt(s)",
      redeliver: "Redeliver",
      redelivered: "Redelivered",
      docs: "Payload format and signature verification",
    },
    activity: {
      title: "Activity",
      desc: "Who did what in this organization (dashboard, API and Tenanci team).",
      empty: "Nothing recorded yet.",
      more: "Load more",
      filter: "Filter by action",
      all: "All",
      groups: { domain: "Domains", project: "Projects", member: "Team", api_key: "API keys", webhook: "Webhooks", billing: "Subscription", tailorkit: "TailorKit", admin: "Administration" },
      adminBadge: "Tenanci team",
    },
    yourRole: "Your role: {role}",
    roles: {
      owner: "Owner",
      admin: "Admin",
      member: "Member",
    },
    orgId: "Organization ID",
  },
  invite: {
    title: "Invitation to {org}",
    desc: "{by} invited you ({email}) to join as {role}.",
    descNoBy: "You ({email}) were invited to join as {role}.",
    accept: "Accept invitation",
    accepted: "Welcome to {org}!",
    login: "Sign in to accept",
    signup: "Create an account with {email}",
    invalid: "This invitation doesn't exist, was already used or has expired. Ask whoever invited you for a new one.",
    wrongEmail: "You signed in with a different email. Sign out and sign in with {email} to accept.",
  },
  blocked: {
    title: "Blocked paths",
    descProject: "Addresses the edge refuses (error 403) on every domain in the project, before reaching the origin. E.g. /admin, /wp-login.php.",
    descDomain: "Only on this domain (added to the project's).",
    hint: "Blocks the path and everything that starts with it: /admin also blocks /admin/users and /administrator.",
    add: "Add",
    placeholder: "/admin",
    empty: "No blocked paths.",
    fromProject: "From the project: {paths}",
    saved: "Blocked paths saved",
    invalid: "Use a path that starts with / (and isn't just /).",
  },
  bug: {
    title: "Report a problem",
    desc: "We'll open your email app with the details already filled in for support@tenanci.com.",
    what: "What happened?",
    whatPlaceholder: "Describe the problem and the steps to reproduce it.",
    expected: "What did you expect to happen?",
    open: "Open email",
    subject: "[Dashboard] Problem: {summary}",
    mail: {
      what: "What happened:",
      expected: "What I expected:",
      context: "— Context (filled in automatically) —",
      page: "Page: {value}",
      org: "Organization: {value}",
      browser: "Browser: {value}",
      date: "Date: {value}",
    },
  },
  notFound: {
    title: "Page not found",
    desc: "The address may be wrong or the page was removed.",
    home: "Go home",
  },
};
